import type Phaser from 'phaser';
import { UNIT_ANIM_FRAMES } from './unitAnimManifest';

/**
 * Frame animation for units and workers (D137). Each sheet is a strip of 8
 * frames generated in Moe's Space (Animation page): frames 0–3 are the walk
 * cycle, 4–7 the attack / work action. Every frame faces right (the renderer
 * mirrors it) and shares one size, with the feet on the same ground line.
 * `tools/process-anim.py` writes the strips and `unitAnimManifest.ts`.
 */
export interface AnimSheet {
  /** Frame size in sheet px. */
  readonly fw: number;
  readonly fh: number;
  /** Height of the standing body in frame 0 (sheet px), for scaling to the unit's size. */
  readonly bodyH: number;
  /** Feet line inside a frame (sheet px from the top). */
  readonly footY: number;
}

export const WALK_FRAMES = 4;
export const ACTION_FIRST = 4;
export const ACTION_FRAMES = 4;

export const animKey = (name: string): string => `anim-${name}`;

export function animSheet(name: string): AnimSheet | null {
  return UNIT_ANIM_FRAMES[name] ?? null;
}

/** Queue every animation strip (call from a scene's preload). */
export function preloadAnimations(scene: Phaser.Scene): void {
  const root = `${import.meta.env?.BASE_URL ?? '/'}art/anim/`;
  for (const [name, s] of Object.entries(UNIT_ANIM_FRAMES)) {
    scene.load.spritesheet(animKey(name), `${root}${name}.webp`, { frameWidth: s.fw, frameHeight: s.fh });
  }
}

/** Walk speed of the cycle (frames per second) by unit / worker name. */
export function walkFps(name: string): number {
  switch (name) {
    case 'runner':
      return 11;
    case 'walker':
    case 'bloater':
    case 'screamer':
      return 5;
    case 'brute':
      return 4.5;
    default:
      return 7;
  }
}
